/* eslint-disable no-param-reassign */
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import {CdkMenu, CdkMenuItem, CdkContextMenuTrigger} from '@angular/cdk/menu';
import { I18n, TranslatePipe } from '@corporate/ng-tebis-base-components';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TabModel, TabService } from './tab.service';

export const globalDraggedTab = signal<{windowIndex: number, tabIndex: number}|null>(null);

type Menu = {
  id: number;
  tabs: TabModel[];
};

@Component({
  selector: 'tps-flat-tabs',
  imports: [
    MatTabsModule,
    CdkContextMenuTrigger,
    CdkMenu,
    CdkMenuItem,
    MatIconModule,
    MatMenuModule,
    TranslatePipe,
  ],
  templateUrl: './flat-tabs.component.html',
  styleUrl: './flat-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlatTabsComponent implements OnInit, OnDestroy {

  i18n = inject(I18n);
  elementRef = inject(ElementRef<HTMLElement>);

  tabService = inject(TabService);

  tabGroup = viewChild<ElementRef<HTMLDivElement>>('tabGroup');

  tabs = computed(() => this.tabService.getCurrentLocation());
  currentPath = computed(() => this.tabService.getCurrentPath());
  currentLocation = computed(() => this.tabService.getCurrentLocation());

  menuRoot = signal<TabModel|null>(null);
  hoveredTabs = signal<TabModel[]>([]);
  menuContent = computed(() => {
    const menus = <Menu[]>[];
    const menuRoot = this.menuRoot();
    if (!menuRoot) {
      return [];
    }
    if (menuRoot.path === 'home') {
      menus.push({id: -1, tabs: [menuRoot]});
    } else if (menuRoot?.parent) {
      menus.push({id: menuRoot.parent.id, tabs: menuRoot.parent.children});
    }
    this.hoveredTabs().forEach(tab => {
      menus.push({id: tab.id, tabs: tab.children});
    });
    menus.forEach(menu => {
      menu.tabs = [
        ...menu.tabs.filter(t => this.currentLocation().includes(t)),
        ...menu.tabs.filter(t => !this.currentLocation().includes(t))
      ];
    });
    return menus;
  });

  constructor() {
    this.i18n.addNamespace('windowtabs');
    this.onClick = this.onClick.bind(this);
  }

  ngOnInit(): void {
    this.menuRoot.set(null);
    this.hoveredTabs.set([]);

    document.addEventListener('click', this.onClick);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.onClick);
  }

  onTabClicked(tab: TabModel) {
    this.menuRoot.set(tab);
    this.hoveredTabs.set([tab]);
  }

  selectTab(tab: TabModel) {
    this.menuRoot.set(null);
    this.hoveredTabs.set([]);
    this.tabService.activate(tab);
  }

  close(tab: TabModel) {
    this.tabService.closeWithDescendants(tab);
  }

  onTabHovered(menu: Menu, tab: TabModel) {
    const menuIndex = this.menuContent().indexOf(menu);
    this.hoveredTabs.update(tabs => {
      const oldTabs = JSON.stringify(tabs.map(t => t.id));
      if (tabs.length > menuIndex) {
        tabs[menuIndex] = tab;
        if (tabs.length > menuIndex + 1) {
          tabs.splice(menuIndex + 1, tabs.length - menuIndex - 1);
        }
      } else if (tabs.length === menuIndex) {
        tabs.push(tab);
      }
      if (oldTabs === JSON.stringify(tabs.map(t => t.id))) {
        return tabs;
      }
      return [...tabs];
    });
  }

  onClick(event: MouseEvent) {
    const tabGroup = this.tabGroup()?.nativeElement;
    if (event.target === tabGroup || (event.composedPath && !event.composedPath().includes(tabGroup || this.elementRef.nativeElement))) {
      this.menuRoot.set(null);
      this.hoveredTabs.set([]);
    }
  }

  getTooltipForTab(tab: TabModel) {
    if (tab.type == 'home' || tab.type == 'none') {
      return `${tab.name()}`;
    }

    let typeName = '';
    switch (tab.type) {
      case 'customer': typeName = this.i18n.translate('customer')(); break;
      case 'order': typeName = this.i18n.translate('manufacturingOrder')(); break;
      case 'article': typeName = this.i18n.translate('article')(); break;
      default: break;
    }
    return `${typeName} ${tab.name()}`;
  }
}
